import { Reveal, SectionTag, StatusPill } from "./Reveal";

const FOCUS = ["AI Entertainment", "Real-Time Video", "Photorealistic AI Characters", "Interactive Virtual Companions"];

const TEAM = [
  {
    initials: "AMP",
    name: "Artur-Mihail Popescu",
    role: "Founder & Director",
    desc: "Strategy, fundraising and company leadership.",
  },
  {
    initials: "CS",
    name: "Claudiu Sfetan",
    role: "Lead MVP Development — AI & Technology",
    desc: "Level 1 MVP development and technical implementation.",
  },
  {
    initials: "CB",
    name: "Ciprian Baran",
    role: "Co-Founder — Business Development & Communications",
    desc: "Business development, communications and support for investor and project presentations.",
  },
];

export default function About() {
  return (
    <section id="about" data-testid="about-section" className="relative py-16 lg:py-36">
      <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-2 lg:px-10">
        <Reveal>
          <SectionTag>Company</SectionTag>
          <h2 className="font-display mt-5 text-3xl font-light tracking-tighter text-white sm:text-4xl lg:text-5xl">
            AIROBOTCAM<span className="text-cyan-300">STUDIO</span> LTD
          </h2>
          <div className="mt-6">
            <StatusPill tone="white">UK Company No. 17410621</StatusPill>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <p className="text-base leading-relaxed text-[#C5CBD5]">
            AIROBOTCAMSTUDIO LTD is a UK technology startup developing interactive AI entertainment.
            The company is building a real-time AI videochat experience with photorealistic virtual
            companions, combining conversational AI, natural voice, emotional reactions, facial
            expressions and realistic movement.
          </p>
          <p className="mt-4 text-base leading-relaxed text-[#C5CBD5]">
            The current focus is the MVP: a working real-time videochat experience with
            photorealistic AI characters. The long-term vision extends the same characters and
            intelligence into physical humanoid AI robots.
          </p>
          <div className="mt-8 flex flex-wrap gap-2.5">
            {FOCUS.map((f) => (
              <span key={f} className="rounded-full border border-white/10 px-4 py-1.5 text-[11px] uppercase tracking-[0.2em] text-white/70">
                {f}
              </span>
            ))}
          </div>
        </Reveal>
      </div>

      <div className="mx-auto mt-24 max-w-7xl px-6 lg:px-10">
        <Reveal>
          <SectionTag>The Team</SectionTag>
          <h3 className="font-display mt-5 max-w-xl text-2xl font-light tracking-tighter text-white sm:text-3xl lg:text-4xl">
            The People Building <span className="text-cyan-300">AIROBOTCAMSTUDIO</span>
          </h3>
        </Reveal>

        <div data-testid="team-grid" className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {TEAM.map(({ initials, name, role, desc }, i) => (
            <Reveal key={name} delay={0.08 * i}>
              <div
                data-testid={`team-card-${name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="h-full rounded-2xl border border-white/10 bg-white/[0.03] p-8"
              >
                <div
                  aria-hidden="true"
                  className="flex h-16 w-16 items-center justify-center rounded-full border border-cyan-300/30 bg-cyan-300/[0.06]"
                >
                  <span className="font-display text-sm font-semibold tracking-[0.2em] text-cyan-200">
                    {initials}
                  </span>
                </div>
                <p className="font-display mt-6 text-lg font-medium tracking-tight text-white">{name}</p>
                <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300/80">
                  {role}
                </p>
                <p className="mt-4 text-sm leading-relaxed text-[#C5CBD5]">{desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <div data-testid="corporate-info-section" className="mx-auto mt-24 max-w-7xl px-6 lg:px-10">
        <Reveal>
          <SectionTag>Corporate Information</SectionTag>
          <h3 className="font-display mt-5 max-w-xl text-2xl font-light tracking-tighter text-white sm:text-3xl lg:text-4xl">
            Two Separately Registered <span className="text-cyan-300">Legal Entities</span>
          </h3>
        </Reveal>

        <div className="mt-12 grid gap-4 lg:grid-cols-2">
          <Reveal>
            <div data-testid="corp-card-uk" className="h-full rounded-2xl border border-white/10 bg-white/[0.03] p-8">
              <p className="font-display text-[11px] font-semibold uppercase tracking-[0.3em] text-[#B9A7FF]">
                United Kingdom
              </p>
              <p className="font-display mt-4 text-xl font-medium tracking-tight text-white">
                AIROBOTCAMSTUDIO LTD
              </p>
              <dl className="mt-5 space-y-2.5 text-sm leading-relaxed">
                <div className="flex flex-wrap gap-x-2">
                  <dt className="text-white/50">Registered in:</dt>
                  <dd className="text-[#C5CBD5]">England and Wales</dd>
                </div>
                <div className="flex flex-wrap gap-x-2">
                  <dt className="text-white/50">Company Number:</dt>
                  <dd className="text-[#C5CBD5]">17410621</dd>
                </div>
                <div className="flex flex-wrap gap-x-2">
                  <dt className="text-white/50">Incorporated:</dt>
                  <dd className="text-[#C5CBD5]">20 August 2026</dd>
                </div>
              </dl>
              <p className="mt-5 border-t border-white/10 pt-5 text-sm leading-relaxed text-[#C5CBD5]">
                Primary UK company for the current Level 1 platform development and proposed
                fundraising.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div data-testid="corp-card-us" className="h-full rounded-2xl border border-white/10 bg-white/[0.03] p-8">
              <p className="font-display text-[11px] font-semibold uppercase tracking-[0.3em] text-[#B9A7FF]">
                United States
              </p>
              <p className="font-display mt-4 text-xl font-medium tracking-tight text-white">
                AI ROBOT CAM STUDIO LLC
              </p>
              <dl className="mt-5 space-y-2.5 text-sm leading-relaxed">
                <div className="flex flex-wrap gap-x-2">
                  <dt className="text-white/50">Registered in:</dt>
                  <dd className="text-[#C5CBD5]">Delaware, United States</dd>
                </div>
                <div className="flex flex-wrap gap-x-2">
                  <dt className="text-white/50">Delaware File Number:</dt>
                  <dd className="text-[#C5CBD5]">10553824</dd>
                </div>
                <div className="flex flex-wrap gap-x-2">
                  <dt className="text-white/50">Formed:</dt>
                  <dd className="text-[#C5CBD5]">19 March 2026</dd>
                </div>
              </dl>
              <p className="mt-5 border-t border-white/10 pt-5 text-sm leading-relaxed text-[#C5CBD5]">
                Separately registered US legal entity. Currently non-trading.
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <p data-testid="corp-entities-statement" className="mt-8 max-w-3xl text-xs leading-relaxed text-white/55">
            AIROBOTCAMSTUDIO LTD and AI ROBOT CAM STUDIO LLC are separately registered legal
            entities. The current Level 1 platform development and proposed SEIS fundraising are
            associated with AIROBOTCAMSTUDIO LTD in the United Kingdom. AI ROBOT CAM STUDIO LLC is
            registered in Delaware, United States, and is currently non-trading.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
